import { authConfig } from '@/features/auth/config'
import type { Project } from '@/types/project'
import { createLogger } from '@/shared/logging/logger'

const logger = createLogger('ProjectSync')

function resolveSyncEndpoint() {
  const baseUrl = authConfig.apiBaseUrl || authConfig.mainSiteUrl
  if (!baseUrl) {
    return ''
  }

  return `${baseUrl}/api/workbench/video/projects`
}

function countSourceMedia(project: Project): number {
  const mediaIds = new Set<string>()

  for (const item of project.timeline?.items || []) {
    if (item.mediaId) {
      mediaIds.add(item.mediaId)
    }
  }

  return mediaIds.size
}

function buildSyncPayload(project: Project) {
  return {
    externalProjectId: project.id,
    title: project.name,
    description: project.description || '',
    width: project.metadata.width,
    height: project.metadata.height,
    fps: project.metadata.fps,
    durationMs: project.duration || 0,
    sourceCount: countSourceMedia(project),
    resultCount: 0,
    failedCount: 0,
    status: 'draft',
    syncPayloadJson: {
      schemaVersion: project.schemaVersion ?? null,
      thumbnailId: project.thumbnailId ?? null,
      rootFolderName: project.rootFolderName ?? null,
      hasTimeline: Boolean(project.timeline),
      updatedAt: project.updatedAt,
    },
  }
}

export async function syncProjectToMainSite(project: Project) {
  const endpoint = resolveSyncEndpoint()
  if (!endpoint) {
    return false
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(buildSyncPayload(project)),
    })

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(text || `同步失败：${response.status}`)
    }

    return true
  } catch (error) {
    logger.warn('Failed to sync project to main site', error)
    return false
  }
}

export async function deleteSyncedProjectFromMainSite(projectId: string) {
  const endpoint = resolveSyncEndpoint()
  if (!endpoint) {
    return false
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ externalProjectId: projectId, deleted: true }),
    })

    return response.ok
  } catch (error) {
    logger.warn('Failed to delete synced project from main site', error)
    return false
  }
}